import type { WhatsAppConversation } from "@repo/common";
import { db } from "@repo/ui";
import {
	collection,
	limit,
	onSnapshot,
	orderBy,
	query,
	where,
} from "firebase/firestore";
import { useEffect, useState } from "react";

export function whatsAppDate(value: unknown): string {
	if (
		value &&
		typeof value === "object" &&
		"toDate" in value &&
		typeof value.toDate === "function"
	)
		return value.toDate().toISOString();
	if (value instanceof Date) return value.toISOString();
	return typeof value === "string" ? value : "";
}
/** Mount-scoped and bounded, with old-company data cleared on company changes. */
export function useWhatsAppConversations(
	companyId?: string | null,
	range?: { from?: Date; to?: Date },
) {
	const from = range?.from
			? new Date(range.from).setHours(0, 0, 0, 0)
			: undefined,
		to = range?.to ? new Date(range.to).setHours(23, 59, 59, 999) : undefined;
	const scope = `${companyId}:${from}:${to}`;
	const [pagination, setPagination] = useState({ scope: "", size: 200 });
	const size = pagination.scope === scope ? pagination.size : 200;
	const [state, setState] = useState<{
		companyId: string | null;
		scope?: string;
		items: WhatsAppConversation[];
		loading: boolean;
		error: boolean;
		more: boolean;
	}>({ companyId: null, items: [], loading: false, error: false, more: false });
	useEffect(() => {
		if (!companyId) return;
		let live = true;
		setState({
			scope,
			companyId,
			items: [],
			loading: true,
			error: false,
			more: false,
		});
		const stop = onSnapshot(
			query(
				collection(db, "companies", companyId, "whatsappConversations"),
				...(from !== undefined
					? [where("lastMessageAt", ">=", new Date(from))]
					: []),
				...(to !== undefined
					? [where("lastMessageAt", "<=", new Date(to))]
					: []),
				orderBy("lastMessageAt", "desc"),
				limit(size + 1),
			),
			(snapshot) => {
				if (!live) return;
				const items = snapshot.docs.slice(0, size).map((doc) => {
					const data = doc.data();
					return {
						...data,
						id: doc.id,
						startedAt: whatsAppDate(data.startedAt),
						lastMessageAt: whatsAppDate(data.lastMessageAt),
						closedAt: data.closedAt ? whatsAppDate(data.closedAt) : undefined,
					} as WhatsAppConversation;
				});
				setState({
					scope,
					companyId,
					items,
					loading: false,
					error: false,
					more: snapshot.docs.length > size,
				});
			},
			() => {
				if (live)
					setState({
						scope,
						companyId,
						items: [],
						loading: false,
						error: true,
						more: false,
					});
			},
		);
		return () => {
			live = false;
			stop();
		};
	}, [companyId, scope, from, to, size]);
	return state.scope === scope
		? { ...state, loadMore: () => setPagination({ scope, size: size + 200 }) }
		: {
				companyId: companyId || null,
				items: [],
				loading: !!companyId,
				error: false,
				more: false,
				loadMore: () => setPagination({ scope, size: size + 200 }),
			};
}
